/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import Iframe from 'react-iframe';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import InputAdornment from '@mui/material/InputAdornment';
import Snackbar from '@mui/material/Snackbar';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDSelect from 'components/MDSelect';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDDatePicker from 'components/MDDatePicker';
import MDDateRange from 'components/MDDateRange';
import TabPanel from 'components/TabPanel/TabPanel';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

import PosPrint from 'layouts/pos/PosPrint';
import stepperName from './SaleOrderStatus';
import SaleOrderList from './SaleOrderList';
import SaleOrderStepper from './SaleOrderStepper';

const initAttr = {
  id: '',
  code: '',
  remark: '',
  type_id: '',
  customer_id: '',
  vat_id: '2',
  status: 'edit',
  branch_receive: '',
  credit_day: '',
  credit_money: '',
  print_url: '',
  print_no: '0',
  detail_no: '0',
  created_at: '',
  updated_at: '',
  account_id: '',
  creditcard_id: '',
  print: '',
  payment_id: '',
  payment_type: 'CASH',
  client_name: '',
  client_tel: '',
  country: '',
  creditcard_no: '',
  shift_code: '',
  cashier_code: '',
  cashier_name: '',
  emp_name: '',
  branch_name: '',
  date_record: Func.dateNow(),
};

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  status: 'ALL',
  user: 'ALL',
  period: 'DATE',
  branch: 'ALL',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

const initNavigate = {
  first: '',
  prev: '',
  next: '',
  last: '',
};

const cboStatus = [
  { id: 'ALL', name: 'ทั้งหมด' },
  { id: 'edit', name: stepperName.edit.name },
  { id: 'order', name: stepperName.order.name },
  { id: 'invoice', name: stepperName.invoice.name },
  { id: 'payment', name: stepperName.payment.name },
  { id: 'shipping', name: stepperName.shipping.name },
  { id: 'success', name: stepperName.success.name },
  { id: 'cancel', name: stepperName.cancel.name },
];

function SaleOrder() {
  const mod = 'sale_order';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [navigate, setNavigate] = useState(initNavigate);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [stepper, setStepper] = useState({});
  const [cboBranch, setCboBranch] = useState([]);
  const [cboPayment, setCboPayment] = useState([]);
  const [cboCreditCard, setCboCreditCard] = useState([]);
  const [cboAccount, setCboAccount] = useState([]);
  const [tab, setTab] = useState(0);
  const [photo, setPhoto] = useState({});
  const [modalPhoto, setModalPhoto] = useState(false);
  const [modalPrint, setModalPrint] = useState(false);
  const listRef = useRef();
  const printRef = useRef();

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.status', label: 'สถานะ', align: 'center' },
    { id: 'tb.id', label: t(`attr.code`), align: 'center' },
    { id: 'tb.date_record', label: 'วันที่', align: 'center' },
    { id: 'tb.client_name', label: 'ลูกค้า', align: 'left' },
    { id: 'tb.branch_id', label: 'สาขา', align: 'left' },
    { id: 'count', label: 'รายการ', align: 'left', sort: false },
    { id: 'tb.user_id', label: 'ผู้ทำรายการ', align: 'left' },
    { id: 'tb.total', label: 'มูลค่า', align: 'right' },
    { id: 'tb.discount', label: 'โปรโมชั่น', align: 'right' },
    { id: 'tb.discount_approve', label: 'ขอส่วนลด', align: 'right' },
    { id: 'tb.discount_coupon', label: 'คูปอง', align: 'right' },
    { id: 'tb.return_total', label: 'คืนสินค้า', align: 'right' },
    { id: 'tb.discount_all', label: 'ลดรวม', align: 'right' },
    { id: 'tb.nettotal', label: 'สุทธิ', align: 'right' },
    { id: 'tb.payment_type', label: 'การชำระ', align: 'center' },
    { id: 'tb.remark', label: 'หมายเหตุ', align: 'left' },
    { id: 'tb.created_at', label: 'เวลา', align: 'center', sort: false },
  ];

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      status: data.status,
      user: data.user,
      period: data.period,
      date_start: data.date_start,
      date_stop: data.date_stop,
      branch: data.branch,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const open = () => {
    setLoading(true);
    setAttr(initAttr);
    setTab(0);

    FetchData('GET', `/${mod}/${id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(result.data);
        setNavigate(result.navigate);
        setStepper(result.stepper);

        setLoading(false);
      }
    });
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const add = (item) => {
    setLoading(true);

    FetchData('PUT', `/${mod}`, app.token, {
      customer_id: item.customer_id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        window.location.replace(`${mod}/${result.id}`);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const print = () => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/print`, app.token, {
      id: attr.id,
      lang: app.lang,
    }).then((result) => {
      setLoading(false);
      if (result.status === 'ok') {
        setAttr({ ...attr, print: result.data.print, printso_no: result.data.printso_no });
        setModalPrint(true);
      }
    });
  };

  const slip = () => {
    printRef.current.print(attr.id);
  };

  const copy = () => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/copy`, app.token, {
          id: id,
        }).then((result) => {
          if (result.status === 'ok') {
            window.location.replace(`/${mod}/${result.id}`);
            showNotify(t(`alert.addcomplete`), 'success');
          } else {
            setLoading(false);
            showNotify(t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.type_id, 1)) return false;

    return true;
  };

  const edit = () => {
    if (!editConfirm()) return;

    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
          remark: attr.remark,
          type_id: attr.type_id,
          date_record: attr.date_record,
          date_due: attr.date_due,
          vat_id: attr.vat_id,
          branchsend_id: attr.branchsend_id,
          payment_type: attr.payment_type,
          shipping_id: attr.shipping_id,
          car_id: attr.car_id,
          date_shipping: attr.date_shipping,
          address: attr.address,
          address2: attr.address2,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.${result.msg}`), 'error');
          }
        });
      }
    });
  };

  const approve = (status) => {
    Swal.fire({
      title: `คุณต้องการเปลี่ยนสถานะเป็น \n"${stepperName[status].name}" ใช่หรือไม่ ?`,
      input: 'text',
      inputAttributes: {
        autocapitalize: 'off',
        placeholder: 'กรุณาป้อนหมายเหตุเมื่อกดยกเลิก',
      },
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        const remark = Swal.getInput()?.value || '';
        if (status === 'cancel' && remark === '') {
          showNotify('กรุณากรอกหมายเหตุ', 'error');
          return;
        }

        setLoading(true);
        FetchData('OPTIONS', `/${mod}/approve`, app.token, {
          id: id,
          status: status,
          remark: remark,
        }).then((result) => {
          if (result.status === 'ok') {
            open();
            listRef.current.view();
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            setLoading(false);
            showNotify(result.msg, 'error');
          }
        });
      }
    });
  };

  const openPhoto = (item) => {
    setPhoto(item);
    setModalPhoto(true);
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(initAttr);
        setCboBranch(result.branch);
        setCboPayment(result.payment);
        setCboAccount(result.account);
        setCboCreditCard(result.creditcard);

        if (id === undefined) {
          view(find);
        } else {
          open();
        }
      } else {
        Func.exitApp();
      }
    });
  };

  const btnOpen = (item) => {
    return (
      <NavLink to={`/${mod}/${item.id}`}>
        <MDButton
          variant='text'
          color='info'
          size='small'
          sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}
        >
          {item.code}
        </MDButton>
      </NavLink>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>{t('element.show_all')}</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.status}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, status: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>สถานะ :</span>
                  </InputAdornment>
                ),
              }}
            >
              {cboStatus.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>

          {find.period === 'ALL' ? (
            <Grid item xs={12} sm={12} md={6} lg={2}>
              <MDButton
                variant='outlined'
                color='secondary'
                fullWidth
                sx={{ p: 1.5 }}
                onClick={() => view({ ...find, period: 'DATE', page: 1 })}
              >
                <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
              </MDButton>
            </Grid>
          ) : null}
          {find.period === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={12} lg={7}>
              <MDDateRange
                dateStart={find.date_start}
                dateStop={find.date_stop}
                callBack={setDateRange}
                closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
              />
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center' width={160}>
                        {Func.statusDisplay(row)}
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox>{btnOpen(row)}</MDBox>
                      </TableCell>
                      <TableCell align='center'>{Func.dateDisplay(row.date_record)}</TableCell>
                      <TableCell>
                        <MDBox className='nowrap'>{row.client_name}</MDBox>
                        <MDBox className='nowrap'>{row.country_name}</MDBox>
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.branch_name}
                      </TableCell>
                      <TableCell align='center'>
                        <code className='color-primary'>{row.count}</code>
                      </TableCell>
                      <TableCell align='left'>{row.user_name}</TableCell>
                      <TableCell align='right' className='color-info'>
                        {Func.numberDisplay(row.total, 2)}
                      </TableCell>
                      <TableCell align='right' className='color-danger'>
                        {Func.numberDisplay(row.discount, 2)}
                      </TableCell>
                      <TableCell align='right' className='color-danger'>
                        {Func.numberDisplay(row.discount_approve, 2)}
                      </TableCell>
                      <TableCell align='right' className='color-danger'>
                        {Func.numberDisplay(row.discount_coupon, 2)}
                      </TableCell>
                      <TableCell align='right' className='color-danger'>
                        {Func.numberDisplay(row.return_total, 2)}
                      </TableCell>
                      <TableCell align='right' className='color-danger'>
                        {Func.numberDisplay(row.discount_all, 2)}
                      </TableCell>
                      <TableCell align='right' className='color-success'>
                        {Func.numberDisplay(row.nettotal, 2)}
                      </TableCell>
                      <TableCell align='center'>{row.payment_name}</TableCell>
                      <TableCell>{row.remark}</TableCell>
                      <TableCell align='center'>{Func.dateTimeDisplay(row.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderStepper = () => {
    const action = (key) => {
      switch (attr.status) {
        case 'edit':
          return key === 'order' || key === 'cancel';
        case 'order':
          return key === 'invoice' || key === 'cancel';
        case 'invoice':
          return key === 'payment' || key === 'cancel';
        case 'payment':
          if (attr.shipping_id === '1') return key === 'success' || key === 'cancel';
          return key === 'shipping' || key === 'cancel';
        case 'shipping':
          return key === 'success' || key === 'cancel';
        default:
          return false;
      }
    };

    const stepLevel = (key) => {
      return (
        <Step sx={{ minWidth: 150 }}>
          <StepLabel StepIconComponent={SaleOrderStepper}>
            {action(key) ? (
              <MDButton
                variant='gradient'
                color={stepperName[key].color}
                size='small'
                onClick={() => approve(key)}
              >
                <Icon>{stepperName[key].icon}</Icon> &nbsp; {stepperName[key].name}
              </MDButton>
            ) : stepper[key] === undefined ? (
              <MDTypography variant='subtitle2'>{stepperName[key].name}</MDTypography>
            ) : attr.status === key ? (
              <MDBox>
                <MDBadge
                  badgeContent={stepperName[key].name}
                  variant='contained'
                  color={stepperName[key].color}
                  size='md'
                  container
                />
                <MDTypography variant='subtitle2'>{stepper[key].emp_name}</MDTypography>
                <MDTypography variant='subtitle2'>
                  {Func.dateTimeDisplay(stepper[key].created_at)}
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <MDTypography variant='h6'>{stepperName[key].name}</MDTypography>
                <MDTypography variant='subtitle2'>{stepper[key].emp_name}</MDTypography>
                <MDTypography variant='subtitle2'>
                  {Func.dateTimeDisplay(stepper[key].created_at)}
                </MDTypography>
              </MDBox>
            )}
          </StepLabel>
        </Step>
      );
    };

    const stepInt = () => {
      if (attr.status === 'waiting') return 2;
      if (attr.status === 'order') return 3;
      if (attr.status === 'invoice') return 4;
      if (attr.status === 'payment') return 5;
      if (attr.status === 'shipping') return 6;
      if (attr.status === 'success') return 7;
      if (attr.status === 'cancel') return 9;

      return 0;
    };

    return loading ? null : (
      <MDBox className='stepper' mt={3} mb={1}>
        <Stepper activeStep={stepInt()} alternativeLabel>
          {stepLevel('edit')}
          {stepLevel('order')}
          {stepLevel('invoice')}
          {stepLevel('payment')}
          {stepLevel('shipping')}
          {stepLevel('success')}
          {stepLevel('cancel')}
        </Stepper>
      </MDBox>
    );
  };

  const renderInfo = () => {
    const readOnly = attr.status === 'success' || attr.status === 'cancel';

    return (
      <MDBox className='card' p={2}>
        <Grid container>
          <Grid item xs={6}>
            <MDTypography variant='h5' mb={3}>
              การขาย
            </MDTypography>
          </Grid>
          <Grid item xs={6}>
            {attr.status === 'success' || attr.status === 'cancel' ? null : (
              <MDBox display='flex' justifyContent='end' gap={1}>
                <MDButton variant='text' color='success' size='small' onClick={edit}>
                  <Icon>save</Icon>&nbsp;{t(`element.save`)}
                </MDButton>
              </MDBox>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput label='สาขา' value={attr.branch_name} variant='standard' multiline fullWidth />
          </Grid>
          <Grid item xs={12}>
            <MDDatePicker
              onChange={([date]) => {
                setAttr({ ...attr, date_record: Func.dateFormat(date) });
              }}
              value={new Date(attr.date_record)}
              input={{
                label: 'วันที่ขาย',
                variant: 'standard',
                fullWidth: true,
              }}
              readOnly={readOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label={t(`attr.remark`)}
              onChange={(e) => setAttr({ ...attr, remark: e.target.value })}
              value={attr.remark}
              variant='standard'
              multiline
              inputProps={{
                maxLength: 200,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='เงินลิ้นชักก่อนคิดเงิน'
              value={Func.numberDisplay(attr.wallet_before)}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
              InputProps={{
                endAdornment: <InputAdornment position='end'>บาท</InputAdornment>,
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='เงินลิ้นชักหลังคิดเงิน'
              value={Func.numberDisplay(attr.wallet_after)}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
              InputProps={{
                endAdornment: <InputAdornment position='end'>บาท</InputAdornment>,
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderMoreInfo = () => {
    const readOnly = attr.status === 'success' || attr.status === 'cancel';

    return (
      <MDBox className='card' p={2}>
        <Grid container>
          <Grid item xs={6}>
            <MDTypography variant='h5' mb={3}>
              การชำระ
            </MDTypography>
          </Grid>
          <Grid item xs={6}>
            {attr.status === 'success' || attr.status === 'cancel' ? null : (
              <MDBox display='flex' justifyContent='end' gap={1}>
                <MDButton variant='text' color='success' size='small' onClick={edit}>
                  <Icon>save</Icon>&nbsp;{t(`element.save`)}
                </MDButton>
              </MDBox>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDSelect
              label='วิธีชำระเงิน'
              labelDefault={t(`element.please_select`)}
              data={cboPayment}
              value={attr.payment_id}
              onChange={(e) => setAttr({ ...attr, payment_id: e.target.value })}
              inputProps={{
                autoComplete: 'off',
                readOnly: readOnly,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelect
              label='บัญชีธนาคาร'
              labelDefault={t(`element.please_select`)}
              data={cboAccount}
              value={attr.account_id}
              onChange={(e) => setAttr({ ...attr, account_id: e.target.value })}
              inputProps={{
                autoComplete: 'off',
                readOnly: readOnly,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelect
              label='บัตร'
              labelDefault={t(`element.please_select`)}
              data={cboCreditCard}
              value={attr.creditcard_id}
              onChange={(e) => setAttr({ ...attr, creditcard_id: e.target.value })}
              inputProps={{
                autoComplete: 'off',
                readOnly: readOnly,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='รหัสท้ายบิลบัตร'
              onChange={(e) => setAttr({ ...attr, creditcard_no: e.target.value })}
              value={attr.creditcard_no}
              variant='standard'
              inputProps={{
                maxLength: 20,
              }}
              fullWidth
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderCustomer = () => {
    return (
      <MDBox className='card' p={2}>
        <MDTypography variant='h5' mb={3}>
          ลูกค้า
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='ชื่อ'
              value={attr.client_name}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='เบอร์โทร'
              value={attr.client_tel}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ประเทศ'
              value={attr.country}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderRound = () => {
    return (
      <MDBox className='card' p={2}>
        <MDTypography variant='h5' mb={3}>
          รอบการขาย
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='รอบการขาย'
              value={attr.shift_code}
              variant='standard'
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='รหัสเครื่อง POS'
              value={attr.cashier_code}
              variant='standard'
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ชื่อเครื่อง POS'
              value={attr.cashier_name}
              variant='standard'
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ผู้ขาย'
              value={attr.emp_name}
              variant='standard'
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const btnPrint = () => {
    return (
      <MDButton variant='gradient' color='info' onClick={print}>
        <Icon>print</Icon>&nbsp;พิมพ์ {attr.printso_no > 0 ? `(${attr.printso_no})` : ''}
      </MDButton>
    );
  };

  const btnSlip = () => {
    return (
      <MDButton variant='gradient' color='primary' onClick={slip}>
        <Icon>print</Icon>&nbsp;Slip {attr.print_no > 0 ? `(${attr.print_no})` : ''}
      </MDButton>
    );
  };

  const renderButton = () => {
    return (
      <MDBox display='flex' justifyContent='end' gap={1}>
        {/* <MDButton variant="gradient" color="warning" onClick={copy}>
          <Icon>content_copy</Icon>&nbsp;{t(`element.copy`)}
        </MDButton> */}
        {btnSlip()}
        {/* {btnPrint()} */}
      </MDBox>
    );
  };

  const renderNavigate = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container>
          <Grid item xs={6}>
            <NavLink to={`/${mod}`}>
              <MDButton variant='contained' color='dark'>
                <Icon>arrow_back</Icon>&nbsp;{t(`element.back`)}
              </MDButton>
            </NavLink>
          </Grid>
          {navigate.first === '' ? null : (
            <Grid item xs={6}>
              <MDBox display='flex' justifyContent='flex-end' gap={1}>
                <NavLink to={`/${mod}/${navigate.first}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.first === id}
                  >
                    <Icon>first_page</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.prev}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.prev === id}
                  >
                    <Icon>navigate_before</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.next}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.next === id}
                  >
                    <Icon>navigate_next</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.last}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.last === id}
                  >
                    <Icon>last_page</Icon>
                  </MDButton>
                </NavLink>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderOpen = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderNavigate()}
          </Grid>
          <Grid item xs={12}>
            {renderStepper()}
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox sx={{ width: '100%' }}>
                <MDBox>
                  <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                    aria-label='tabs-addedit'
                    sx={{ overflowX: 'scroll' }}
                  >
                    <Tab label='ใบสั่งขาย' />
                  </Tabs>
                </MDBox>
                <TabPanel value={tab} index={0}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6} xl={6}>
                      <MDTypography variant='h3'>#{attr.code} &nbsp;</MDTypography>
                    </Grid>
                    <Grid item xs={12} md={6} xl={6}>
                      {renderButton()}
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={3}>
                      {renderInfo()}
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={3}>
                      {renderMoreInfo()}
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={3}>
                      {renderCustomer()}
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={3}>
                      {renderRound()}
                    </Grid>
                    <Grid item xs={12}>
                      <SaleOrderList parentId={id} ref={listRef} />
                    </Grid>
                  </Grid>
                </TabPanel>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPhoto = () => {
    return (
      <Dialog
        open={modalPhoto}
        maxWidth='md'
        keepMounted
        aria-describedby='dialog-image'
        fullWidth
        onClose={() => setModalPhoto(false)}
      >
        <DialogTitle>
          {Func.dateTimeDisplay(photo.created_at)}
          <IconButton
            aria-label='close'
            onClick={() => setModalPhoto(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox
            component='img'
            src={photo.image}
            alt=''
            borderRadius='lg'
            shadow='sm'
            width='100%'
            position='relative'
          />
        </DialogContent>
      </Dialog>
    );
  };

  const renderPrint = () => {
    return (
      <Dialog
        open={modalPrint}
        maxWidth='xl'
        keepMounted
        aria-describedby='dialog-print'
        fullScreen
        onClose={() => setModalPrint(false)}
      >
        <DialogTitle>
          #{attr.code}
          <IconButton
            aria-label='close'
            onClick={() => setModalPrint(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {attr.print === '' ? null : (
            <Iframe
              url={attr.print}
              width='100%'
              height='100%'
              id='print'
              className=''
              display='initial'
              position='relative'
              frameBorder='0'
            />
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const renderPage = () => {
    if (id === undefined) return renderList();
    return renderOpen();
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.sale`), t(`menu.sale_order`)]} />
      {renderLoading()}
      {renderPage()}
      {renderPhoto()}
      {renderPrint()}
      <PosPrint ref={printRef} paymentType={attr.payment_type} />
      <Footer />
      {notify}
    </DashboardLayout>
  );
}

export default SaleOrder;
